import { useContext, useState } from 'react';

import axios from 'axios';
import { useFormik } from 'formik';
import { useRouter } from 'next/router';
import { useTranslation } from 'next-i18next';
import * as Yup from 'yup';
import { ToastMessageContext } from 'common/components/ToastMessage';

export const useHook = ({ isProject }: { isProject: boolean }) => {
  const router = useRouter();
  const { i18n, t } = useTranslation('pageContact');

  const { showMessage } = useContext(ToastMessageContext);

  const [isSubmitting, setIsSubmitting] = useState<boolean>(false);

  const getInitialValues = () => {
    const commonValues = {
      about: '',
      companyName: '',
      email: '',
      name: '',
      phoneNumber: '',
      howDidYouHearAboutUs: '',
      message: '',
    };
    if (isProject) {
      return { ...commonValues, services: [], typeOfProject: null };
    }
    return commonValues;
  };

  const getSchemaObject = (): any => {
    if (isProject) {
      return {
        about: Yup.string(),
        name: Yup.string().required(t('form.name.required')),
        email: Yup.string().email(t('form.email.invalid')).required(t('form.email.required')),
        phoneNumber: Yup.string(),
        companyName: Yup.string(),
        howDidYouHearAboutUs: Yup.string(),
        message: Yup.string().required(t('form.message.required')),
        typeOfProject: Yup.object().required(t('form.typeOfProject.required')),
        services: Yup.array()
          .min(1, t('form.service.required'))
          .required(t('form.service.required')),
      };
    }
    return {
      about: Yup.string(),
      companyName: Yup.string(),
      email: Yup.string().email(t('form.email.invalid')).required(t('form.email.required')),
      howDidYouHearAboutUs: Yup.string(),
      name: Yup.string().required(t('form.name.required')),
      phoneNumber: Yup.string(),
      message: Yup.string().required(t('form.message.required')),
    };
  };

  const schema: any = Yup.object().shape(getSchemaObject());

  const handleSave = async (values: any, form: any) => {
    if (isSubmitting) {
      return;
    }
    const {
      about,
      companyName,
      email,
      howDidYouHearAboutUs,
      name,
      phoneNumber,
      message,
      typeOfProject,
    } = values;
    if (about.length) {
    } else {
      const data = {
        about,
        companyName,
        email,
        howDidYouHearAboutUs,
        name,
        phoneNumber,
        message,
        typeOfProject: typeOfProject?.label || '',
        services: values.services?.length
          ? values.services.map((x: any) => x.label).join(', ')
          : '',
      };

      setIsSubmitting(true);
      try {
        const res = await axios.post('https://app.nordit.hr/mail/nordit-contact-form', data);
        setIsSubmitting(false);
        if (res.data.success) {
          form.setValues(getInitialValues());

          // GA4 event
          if (typeof window !== 'undefined' && window) {
            const win: any = window;
            win.dataLayer = win.dataLayer || [];
            win.dataLayer.push({
              event: isProject ? 'contact-project-form-submit' : 'contact-form-submit',
              email: data.email,
            });
          }
          if (i18n.language === 'en') {
            router.push({ pathname: `/thank-you${isProject ? '-project' : ''}` }, undefined, {
              shallow: true,
            });
          } else {
            router.push(
              { pathname: `/${i18n.language}/thank-you${isProject ? '-projekt' : ''}` },
              undefined,
              { shallow: true },
            );
          }
        } else if (res.data.error) {
          showMessage({
            duration: 5000,
            message: `${t('errors.thereAreSomeErrorsInYourForm')} ${t(
              'errors.pleaseCheckTheFormAndTryAgain',
            )}`,
            type: 'error',
          });
        }
      } catch (error: any) {
        setIsSubmitting(false);
        let messages: string[] = [];
        const { message } = error?.response?.data || {};
        if (Array.isArray(message)) {
          message.forEach((item: string) => {
            messages.push(t(`errors.${item}`));
          });
        }

        if (messages.length) {
          showMessage({ duration: 5000, message: messages.join('<br />'), type: 'error' });
        } else {
          showMessage({
            duration: 5000,
            message: `${t('errors.thereAreSomeErrorsInYourForm')} ${t(
              'errors.pleaseCheckTheFormAndTryAgain',
            )}`,
            type: 'error',
          });
        }
      }
    }
  };

  const formik = useFormik({
    initialValues: getInitialValues(),
    validationSchema: schema,
    validateOnChange: false,
    validateOnMount: false,
    onSubmit: handleSave,
  });

  const fieldProps = (field: string) => {
    const data: any = formik.getFieldProps(field);
    const { error: fieldError, touched } = formik.getFieldMeta(field);

    data.onChangeText = (v: any): void => {
      formik.handleChange(field)({ target: { value: v } } as any);
      if (fieldError) {
        formik.validateField(field);
      }
    };
    if (field === 'typeOfProject' || field === 'service') {
      data.onChange = (v: any): void => {
        formik.handleChange(field)({ target: { value: v } } as any);
        if (fieldError) {
          formik.validateField(field);
        }
      };
    }
    return { ...data, error: fieldError, touched, disabled: isSubmitting };
  };

  const handleServiceClick = (item: { label: string; value: string }) => () => {
    const values = formik.values as any;
    let value: { label: string; value: string }[] = values.services;
    if (value.some(x => x.value === item.value)) {
      value = value.filter(x => x.value !== item.value);
    } else {
      value.push(item);
    }
    formik.setFieldValue('services', value);
    formik.validateField('services');
  };

  return {
    handleServiceClick,
    isSubmitting,
    fieldProps,
    formik,
    schema,
  };
};
